/**
 * Presentational dictation status that covers an empty chat prompt editor or
 * renders as a row before existing prompt content. Shows a "Listening…" label
 * while recording and a "Transcribing" label with an animated indicator while
 * the recorded audio is being converted to text.
 *
 * NOTE: this file intentionally does NOT import from `@atlassian/ai-audio`.
 * Only the callback bridge in `voice-enabled-footer-row/index.tsx` may pull
 * from that package to keep it lazy-loaded. The `DictationSessionStatus` type
 * import here is type-only and is erased at compile time.
 */
/* eslint-disable @atlaskit/platform/one-value-export-per-file -- This module owns a single cohesive dictation-status concern: the `DictationStatusDisplay` component, the `useDictationStatus` hook, and the `isDictationStatusVisible` helper all share the same `DictationSessionStatus` value contract and are meant to be consumed together. */
import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { DictationSessionStatus } from '@atlassian/ai-audio/ui/dictation-controls';
import { EllipsesAnimation } from '@atlassian/rovo-platform-ui-components/primitives/EllipsesAnimation';
import { ShimmerText } from '@atlassian/rovo-platform-ui-components/primitives/ShimmerText';

import messages from './messages';
import type { DictationOverlayBackground } from './types';

const overlayBackgroundStyles = cssMap({
	surface: {
		backgroundColor: token('elevation.surface'),
	},
	'surface-raised': {
		backgroundColor: token('elevation.surface.raised'),
	},
	'surface-sunken': {
		backgroundColor: token('elevation.surface.sunken'),
	},
});

const styles = cssMap({
	overlay: {
		position: 'absolute',
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
		zIndex: 1,
	},
	label: {
		color: token('color.text.subtlest'),
		font: token('font.body'),
		paddingBlockStart: token('space.075'),
		paddingInlineStart: token('space.075'),
	},
});

type DictationStatusDisplayProps = {
	appearance: 'overlay' | 'row';
	status: DictationSessionStatus;
	/**
	 * The background token the overlay should use to match the surface behind
	 * the prompt input. Defaults to 'surface'.
	 */
	backgroundOverride?: DictationOverlayBackground;
};

export const isDictationStatusVisible = (status: DictationSessionStatus): boolean =>
	status === 'recording' || status === 'processing';

/**
 * Tracks the current dictation status so the editor can display "Listening"
 * or "Transcribing" as either an overlay or a status row.
 *
 * Lives above the lazy `VoiceEnabledFooterRow` because the status renders
 * inside the editor Box, which is a sibling of the footer row.
 */
export const useDictationStatus = (): {
	dictationStatus: DictationSessionStatus;
	handleDictationStatusChange: (status: DictationSessionStatus) => void;
} => {
	const [dictationStatus, setDictationStatus] = useState<DictationSessionStatus>('idle');
	const handleDictationStatusChange = useCallback((status: DictationSessionStatus) => {
		setDictationStatus(status);
	}, []);
	return { dictationStatus, handleDictationStatusChange };
};

export const DictationStatusDisplay = ({
	appearance,
	status,
	backgroundOverride = 'surface',
}: DictationStatusDisplayProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!isDictationStatusVisible(status)) {
		return null;
	}

	return (
		<Box
			role="status"
			xcss={cx(
				appearance === 'overlay' && styles.overlay,
				appearance === 'overlay' && overlayBackgroundStyles[backgroundOverride],
				styles.label,
			)}
			testId="dictation-status-display"
		>
			{status === 'recording' ? (
				formatMessage(messages.dictationListeningLabel)
			) : (
				<Inline alignBlock="center" space="space.025">
					<ShimmerText>{formatMessage(messages.dictationTranscribingLabel)}</ShimmerText>
					<Box>
						<EllipsesAnimation />
					</Box>
				</Inline>
			)}
		</Box>
	);
};
